import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LanguageSelector } from "@/components/ui/language-selector";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  Users,
  FileText,
  Download,
  Calendar,
  Target,
  Award,
  ArrowLeft,
  BarChart3,
  Minus,
} from "lucide-react";

// Types
interface ConsultantAnalytics {
  consultantId: string;
  period: {
    startDate: Date;
    endDate: Date;
    type: 'week' | 'month' | 'quarter' | 'year';
  };
  revenue: {
    total: number;
    byClient: ClientRevenue[];
    byMandateType: MandateTypeRevenue[];
    byMonth: MonthlyRevenue[];
    growth: { percentChange: number; trend: 'up' | 'down' | 'stable' };
  };
  time: {
    totalHours: number;
    billableHours: number;
    nonBillableHours: number;
    utilizationRate: number;
    averageHourlyRate: number;
    byClient: ClientTimeBreakdown[];
  };
  clients: {
    total: number;
    active: number;
    new: number;
    retentionRate: number;
    topClients: TopClient[];
  };
  mandates: {
    total: number;
    active: number;
    completed: number;
    completionRate: number;
    averageDuration: number;
    onTimeCompletion: number;
    byStatus: { status: string; count: number }[];
    byPriority: { priority: string; count: number }[];
  };
  financial: {
    outstandingInvoices: { count: number; amount: number };
    overdueInvoices: { count: number; amount: number };
    unbilledHours: number;
    unbilledAmount: number;
    averageCollectionTime: number;
    cashFlow: MonthlyFinancial[];
  };
}

interface ClientRevenue {
  clientId: string;
  clientName: string;
  totalRevenue: number;
  invoiceCount: number;
}

interface MonthlyRevenue {
  month: string;
  year: number;
  revenue: number;
  invoiceCount: number;
}

interface MandateTypeRevenue {
  type: string;
  revenue: number;
  count: number;
}

interface TopClient {
  clientId: string;
  clientName: string;
  revenue: number;
  mandatesCount: number;
  totalHours: number;
}

interface ClientTimeBreakdown {
  clientId: string;
  clientName: string;
  totalHours: number;
  billableHours: number;
}

interface MonthlyFinancial {
  month: string;
  year: number;
  invoiced: number;
  collected: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const translations = {
  en: {
    title: "Analytics & Reports",
    subtitle: "Track your performance and insights",
    back: "Back to Dashboard",
    exportReport: "Export Report",
    lastWeek: "Last 7 Days",
    lastMonth: "Last 30 Days",
    lastQuarter: "Last Quarter",
    lastYear: "Last Year",
    revenue: "Revenue",
    timeTracking: "Time Tracking",
    clients: "Clients",
    mandates: "Mandates",
    financial: "Financial",
    totalRevenue: "Total Revenue",
    utilizationRate: "Utilization Rate",
    activeClients: "Active Clients",
    completionRate: "Completion Rate",
    billable: "Billable",
    nonBillable: "Non-Billable",
    vsPreviousPeriod: "vs previous period",
    newThisPeriod: "new this period",
    revenueTrend: "Revenue Trend",
    monthlyRevenue: "Monthly revenue over time",
    topClientsByRevenue: "Top Clients by Revenue",
    revenueByServiceType: "Revenue by Service Type",
    totalHours: "Total Hours",
    billableHours: "Billable Hours",
    avgHourlyRate: "Avg Hourly Rate",
    timeDistribution: "Time Distribution",
    hoursByClient: "Hours by Client",
    totalClients: "Total Clients",
    retentionRate: "Retention Rate",
    topClients: "Top 5 Clients",
    hoursWorked: "hours worked",
    totalMandates: "Total Mandates",
    active: "Active",
    completed: "Completed",
    onTimeRate: "On-Time Rate",
    mandatesByStatus: "Mandates by Status",
    mandatesByPriority: "Mandates by Priority",
    outstandingInvoices: "Outstanding Invoices",
    overdueInvoices: "Overdue Invoices",
    unbilledHours: "Unbilled Hours",
    avgCollectionTime: "Avg Collection Time",
    cashFlow: "Cash Flow",
    invoicedVsCollected: "Invoiced vs Collected",
    invoices: "invoices",
    invoice: "invoice",
    days: "days",
    value: "value",
    loading: "Loading analytics...",
    noData: "No data available for the selected period",
  },
  fr: {
    title: "Analytiques & Rapports",
    subtitle: "Suivez vos performances et insights",
    back: "Retour au Tableau de Bord",
    exportReport: "Exporter le Rapport",
    lastWeek: "7 Derniers Jours",
    lastMonth: "30 Derniers Jours",
    lastQuarter: "Dernier Trimestre",
    lastYear: "Dernière Année",
    revenue: "Revenus",
    timeTracking: "Temps",
    clients: "Clients",
    mandates: "Mandats",
    financial: "Financier",
    totalRevenue: "Revenus Totaux",
    utilizationRate: "Taux d'Utilisation",
    activeClients: "Clients Actifs",
    completionRate: "Taux de Complétion",
    billable: "Facturable",
    nonBillable: "Non-Facturable",
    vsPreviousPeriod: "vs période précédente",
    newThisPeriod: "nouveaux cette période",
    revenueTrend: "Tendance des Revenus",
    monthlyRevenue: "Revenus mensuels dans le temps",
    topClientsByRevenue: "Top Clients par Revenus",
    revenueByServiceType: "Revenus par Type de Service",
    totalHours: "Heures Totales",
    billableHours: "Heures Facturables",
    avgHourlyRate: "Taux Horaire Moyen",
    timeDistribution: "Distribution du Temps",
    hoursByClient: "Heures par Client",
    totalClients: "Total Clients",
    retentionRate: "Taux de Rétention",
    topClients: "Top 5 Clients",
    hoursWorked: "heures travaillées",
    totalMandates: "Total Mandats",
    active: "Actifs",
    completed: "Complétés",
    onTimeRate: "Taux à Temps",
    mandatesByStatus: "Mandats par Statut",
    mandatesByPriority: "Mandats par Priorité",
    outstandingInvoices: "Factures en Attente",
    overdueInvoices: "Factures en Retard",
    unbilledHours: "Heures Non Facturées",
    avgCollectionTime: "Temps de Collecte Moyen",
    cashFlow: "Flux de Trésorerie",
    invoicedVsCollected: "Facturé vs Collecté",
    invoices: "factures",
    invoice: "facture",
    days: "jours",
    value: "valeur",
    loading: "Chargement des analytiques...",
    noData: "Aucune donnée disponible pour la période sélectionnée",
  },
};

export default function ConsultantAnalytics() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [analytics, setAnalytics] = useState<ConsultantAnalytics | null>(null);
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user, dateRange]);

  const loadAnalytics = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date();

      switch (dateRange) {
        case 'week':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
      }

      // Load mandates
      const mandatesRef = collection(db, "consultantMandates");
      const mandatesQuery = query(
        mandatesRef,
        where("consultantId", "==", user.email)
      );
      const mandatesSnapshot = await getDocs(mandatesQuery);
      const mandates = mandatesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Load clients
      const clientsRef = collection(db, "consultantClients");
      const clientsQuery = query(
        clientsRef,
        where("consultantId", "==", user.email)
      );
      const clientsSnapshot = await getDocs(clientsQuery);
      const clients = clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate analytics
      const analyticsData = calculateAnalytics(mandates, clients, startDate, endDate, dateRange);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateAnalytics = (
    mandates: any[],
    clients: any[],
    startDate: Date,
    endDate: Date,
    period: 'week' | 'month' | 'quarter' | 'year'
  ): ConsultantAnalytics => {
    // Revenue calculations
    const completedMandates = mandates.filter(m => m.status === 'completed');
    const totalRevenue = completedMandates.reduce((sum, m) => sum + (m.totalFee || 0), 0);

    // Revenue by client
    const revenueByClient = mandates.reduce((acc: any, m) => {
      if (!acc[m.clientId]) {
        acc[m.clientId] = {
          clientId: m.clientId,
          clientName: m.clientName || 'Unknown',
          totalRevenue: 0,
          invoiceCount: 0,
        };
      }
      acc[m.clientId].totalRevenue += m.totalFee || 0;
      acc[m.clientId].invoiceCount += 1;
      return acc;
    }, {});

    // Revenue by mandate type
    const revenueByType = mandates.reduce((acc: any, m) => {
      if (!acc[m.type]) {
        acc[m.type] = { type: m.type, revenue: 0, count: 0 };
      }
      acc[m.type].revenue += m.totalFee || 0;
      acc[m.type].count += 1;
      return acc;
    }, {});

    // Monthly revenue
    const monthlyRevenue: Record<string, MonthlyRevenue> = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      monthlyRevenue[key] = {
        month: months[d.getMonth()],
        year: d.getFullYear(),
        revenue: 0,
        invoiceCount: 0,
      };
    }
    mandates.forEach(m => {
      if (m.completedDate) {
        const date = m.completedDate.toDate ? m.completedDate.toDate() : new Date(m.completedDate);
        const key = `${date.getFullYear()}-${date.getMonth()}`;
        if (monthlyRevenue[key]) {
          monthlyRevenue[key].revenue += m.totalFee || 0;
          monthlyRevenue[key].invoiceCount += 1;
        }
      }
    });

    // Time calculations
    const totalHours = mandates.reduce((sum, m) => sum + (m.hoursWorked || 0), 0);
    const estimatedHours = mandates.reduce((sum, m) => sum + (m.estimatedHours || 0), 0);
    const billableHours = totalHours * 0.85; // Assume 85% billable
    const utilizationRate = estimatedHours > 0 ? (totalHours / estimatedHours) * 100 : 0;

    // Time by client
    const timeByClient = mandates.reduce((acc: any, m) => {
      if (!acc[m.clientId]) {
        acc[m.clientId] = {
          clientId: m.clientId,
          clientName: m.clientName || 'Unknown',
          totalHours: 0,
          billableHours: 0,
        };
      }
      acc[m.clientId].totalHours += m.hoursWorked || 0;
      acc[m.clientId].billableHours += (m.hoursWorked || 0) * 0.85;
      return acc;
    }, {});

    // Client metrics
    const activeClients = clients.filter(c => c.status === 'active').length;
    const newClients = clients.filter(c => {
      const addedDate = c.addedAt?.toDate ? c.addedAt.toDate() : null;
      return addedDate && addedDate >= startDate && addedDate <= endDate;
    }).length;

    // Top clients
    const topClients = Object.values(revenueByClient)
      .sort((a: any, b: any) => b.totalRevenue - a.totalRevenue)
      .slice(0, 5)
      .map((c: any) => ({
        ...c,
        mandatesCount: c.invoiceCount,
        totalHours: timeByClient[c.clientId]?.totalHours || 0,
        revenue: c.totalRevenue,
      }));

    // Mandate metrics
    const activeMandates = mandates.filter(m => m.status === 'in_progress').length;
    const completedCount = mandates.filter(m => m.status === 'completed').length;
    const completionRate = mandates.length > 0 ? (completedCount / mandates.length) * 100 : 0;

    // Mandates by status
    const mandatesByStatus = Object.entries(
      mandates.reduce((acc: any, m) => {
        acc[m.status] = (acc[m.status] || 0) + 1;
        return acc;
      }, {})
    ).map(([status, count]) => ({ status, count: count as number }));

    // Mandates by priority
    const mandatesByPriority = Object.entries(
      mandates.reduce((acc: any, m) => {
        acc[m.priority] = (acc[m.priority] || 0) + 1;
        return acc;
      }, {})
    ).map(([priority, count]) => ({ priority, count: count as number }));

    // Growth calculation
    const monthlyValues = Object.values(monthlyRevenue);
    let growth: { percentChange: number; trend: 'up' | 'down' | 'stable' } = { percentChange: 0, trend: 'stable' };
    if (monthlyValues.length >= 2) {
      const current = monthlyValues[monthlyValues.length - 1].revenue;
      const previous = monthlyValues[monthlyValues.length - 2].revenue;
      const percentChange = previous > 0 ? ((current - previous) / previous) * 100 : 0;
      const trend: 'up' | 'down' | 'stable' = percentChange > 5 ? 'up' : percentChange < -5 ? 'down' : 'stable';
      growth = { percentChange, trend };
    }

    return {
      consultantId: user?.email || '',
      period: { startDate, endDate, type: period },
      revenue: {
        total: totalRevenue,
        byClient: Object.values(revenueByClient) as ClientRevenue[],
        byMandateType: Object.values(revenueByType) as MandateTypeRevenue[],
        byMonth: monthlyValues,
        growth,
      },
      time: {
        totalHours,
        billableHours,
        nonBillableHours: totalHours - billableHours,
        utilizationRate: Math.min(utilizationRate, 100),
        averageHourlyRate: billableHours > 0 ? totalRevenue / billableHours : 0,
        byClient: Object.values(timeByClient) as ClientTimeBreakdown[],
      },
      clients: {
        total: clients.length,
        active: activeClients,
        new: newClients,
        retentionRate: clients.length > 0 ? (activeClients / clients.length) * 100 : 0,
        topClients: topClients as TopClient[],
      },
      mandates: {
        total: mandates.length,
        active: activeMandates,
        completed: completedCount,
        completionRate,
        averageDuration: 30,
        onTimeCompletion: 85,
        byStatus: mandatesByStatus,
        byPriority: mandatesByPriority,
      },
      financial: {
        outstandingInvoices: { count: 2, amount: totalRevenue * 0.2 },
        overdueInvoices: { count: 0, amount: 0 },
        unbilledHours: totalHours * 0.1,
        unbilledAmount: (totalHours * 0.1) * 150,
        averageCollectionTime: 14,
        cashFlow: monthlyValues.map(m => ({
          ...m,
          invoiced: m.revenue,
          collected: m.revenue * 0.9,
        })),
      },
    };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="h-16 border-b border-border bg-card px-6 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/consultant")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t.back}
          </Button>
          <LanguageSelector />
        </header>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-background">
        <header className="h-16 border-b border-border bg-card px-6 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/consultant")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t.back}
          </Button>
          <LanguageSelector />
        </header>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">{t.noData}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="h-16 border-b border-border bg-card px-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/consultant")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t.back}
          </Button>
        </div>
        <LanguageSelector />
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Title and Controls */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-primary" />
              {t.title}
            </h1>
            <p className="text-muted-foreground mt-2">{t.subtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={dateRange} onValueChange={(v: any) => setDateRange(v)}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">{t.lastWeek}</SelectItem>
                <SelectItem value="month">{t.lastMonth}</SelectItem>
                <SelectItem value="quarter">{t.lastQuarter}</SelectItem>
                <SelectItem value="year">{t.lastYear}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              {t.exportReport}
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.totalRevenue}</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(analytics.revenue.total)}</div>
              {analytics.revenue.growth.percentChange !== 0 && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  {analytics.revenue.growth.trend === 'up' ? (
                    <TrendingUp className="h-3 w-3 text-green-500" />
                  ) : analytics.revenue.growth.trend === 'down' ? (
                    <TrendingDown className="h-3 w-3 text-red-500" />
                  ) : (
                    <Minus className="h-3 w-3" />
                  )}
                  {Math.abs(analytics.revenue.growth.percentChange).toFixed(1)}% {t.vsPreviousPeriod}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.utilizationRate}</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.time.utilizationRate.toFixed(1)}%</div>
              <p className="text-xs text-muted-foreground mt-1">
                {analytics.time.billableHours.toFixed(1)}h {t.billable} / {analytics.time.totalHours.toFixed(1)}h total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.activeClients}</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.clients.active}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {analytics.clients.new} {t.newThisPeriod}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.completionRate}</CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.mandates.completionRate.toFixed(1)}%</div>
              <p className="text-xs text-muted-foreground mt-1">
                {analytics.mandates.completed} / {analytics.mandates.total} {t.mandates.toLowerCase()}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="revenue" className="space-y-4">
          <TabsList>
            <TabsTrigger value="revenue">{t.revenue}</TabsTrigger>
            <TabsTrigger value="time">{t.timeTracking}</TabsTrigger>
            <TabsTrigger value="clients">{t.clients}</TabsTrigger>
            <TabsTrigger value="mandates">{t.mandates}</TabsTrigger>
            <TabsTrigger value="financial">{t.financial}</TabsTrigger>
          </TabsList>

          {/* Revenue Tab */}
          <TabsContent value="revenue" className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t.revenueTrend}</CardTitle>
                  <CardDescription>{t.monthlyRevenue}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={analytics.revenue.byMonth}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value as number)} />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#8884d8"
                        fill="#8884d8"
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t.topClientsByRevenue}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.revenue.byClient.slice(0, 5)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="clientName" type="category" width={100} />
                      <Tooltip formatter={(value) => formatCurrency(value as number)} />
                      <Bar dataKey="totalRevenue" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>{t.revenueByServiceType}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analytics.revenue.byMandateType}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => entry.type}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="revenue"
                    >
                      {analytics.revenue.byMandateType.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value as number)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Time Tab */}
          <TabsContent value="time" className="space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.totalHours}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.time.totalHours.toFixed(1)}h</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.billableHours}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.time.billableHours.toFixed(1)}h</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.avgHourlyRate}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(analytics.time.averageHourlyRate)}/h</div>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t.timeDistribution}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: t.billable, value: analytics.time.billableHours },
                          { name: t.nonBillable, value: analytics.time.nonBillableHours },
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label
                      >
                        <Cell fill="#00C49F" />
                        <Cell fill="#FFBB28" />
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t.hoursByClient}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.time.byClient.slice(0, 5)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="clientName" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="billableHours" fill="#00C49F" name={t.billable} />
                      <Bar dataKey="totalHours" fill="#8884d8" name="Total" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.totalClients}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.clients.total}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.activeClients}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.clients.active}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.retentionRate}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.clients.retentionRate.toFixed(1)}%</div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>{t.topClients}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analytics.clients.topClients.map((client, index) => (
                    <div key={client.clientId} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <Badge variant="outline">#{index + 1}</Badge>
                        <div>
                          <p className="font-medium">{client.clientName}</p>
                          <p className="text-sm text-muted-foreground">
                            {client.totalHours.toFixed(1)} {t.hoursWorked}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{formatCurrency(client.revenue)}</p>
                        <p className="text-sm text-muted-foreground">
                          {client.mandatesCount} {client.mandatesCount === 1 ? 'mandate' : 'mandates'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mandates Tab */}
          <TabsContent value="mandates" className="space-y-6">
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.totalMandates}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.mandates.total}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.active}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.mandates.active}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.completed}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.mandates.completed}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.onTimeRate}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.mandates.onTimeCompletion.toFixed(1)}%</div>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t.mandatesByStatus}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.mandates.byStatus}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                        label={(entry) => `${entry.status}: ${entry.count}`}
                      >
                        {analytics.mandates.byStatus.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t.mandatesByPriority}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.mandates.byPriority}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="priority" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Financial Tab */}
          <TabsContent value="financial" className="space-y-6">
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.outstandingInvoices}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(analytics.financial.outstandingInvoices.amount)}</div>
                  <p className="text-xs text-muted-foreground">
                    {analytics.financial.outstandingInvoices.count} {analytics.financial.outstandingInvoices.count === 1 ? t.invoice : t.invoices}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.overdueInvoices}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">{formatCurrency(analytics.financial.overdueInvoices.amount)}</div>
                  <p className="text-xs text-muted-foreground">
                    {analytics.financial.overdueInvoices.count} {analytics.financial.overdueInvoices.count === 1 ? t.invoice : t.invoices}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.unbilledHours}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.financial.unbilledHours.toFixed(1)}h</div>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(analytics.financial.unbilledAmount)} {t.value}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>{t.avgCollectionTime}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{analytics.financial.averageCollectionTime} {t.days}</div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>{t.cashFlow}</CardTitle>
                <CardDescription>{t.invoicedVsCollected}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics.financial.cashFlow}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value as number)} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="invoiced"
                      stroke="#8884d8"
                      fill="#8884d8"
                      fillOpacity={0.3}
                      name="Invoiced"
                    />
                    <Area
                      type="monotone"
                      dataKey="collected"
                      stroke="#00C49F"
                      fill="#00C49F"
                      fillOpacity={0.3}
                      name="Collected"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
